/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Flex,
  Text,
  IconButton,
  Stack,
  Collapse,
  Icon,
  Popover,
  PopoverTrigger,
  PopoverContent,
  useColorModeValue,
  useDisclosure,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Link,
  Center,
  Circle,
  Button
} from '@chakra-ui/react';
import {
  HamburgerIcon,
  CloseIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@chakra-ui/icons';
import {
  BellIcon,
  CodeQuickIcon,
  LogoutIcon,
  UserIcon,
} from '../../constants/IconData';
import AppColors from '../../constants/AppColors';
import ROUTES from '../../constants/Routes';
import React, { useContext, useEffect, useMemo } from 'react';
import { NavLink, useMatch, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../providers/AuthProvider';
import { replaceRoute, toTitleCase } from '../../utils/common.utils';
import { withProvider } from '../../hoc/withProvider';
import NotificationProvider, {
  NotificationsContext,
} from '../../providers/NotificationProvider';
import { formatDistanceStrict } from 'date-fns';
import { FONT_FAMILY } from '../../constants/Theme';
import { useInterval } from '../../hooks/useInterval';
import { ACCESS_TOKEN } from '../../constants/PreferenceKeys';

const PORTAL_VIEWS = [
  { id: process.env.REACT_APP_AUDITOR_REDIRECT_URL, name: 'AUDITOR' },
  { id: process.env.REACT_APP_QA_REDIRECT_URL, name: 'QA' },
];


const ViewOptionBtn = ({
  buttonMsg = 'View',
  emptyOptionsMsg = 'No Data Available',
  options = null,
}) => {
  const handleSelectOption = (optionUrl) => {
    if (optionUrl) {
      const url = `${optionUrl}?accessToken=${localStorage.getItem(ACCESS_TOKEN)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  if (!options) {
    return 'Loading...';
  }

  return (
    <Menu>
      <MenuButton
        as={Button}
        rightIcon={<ChevronDownIcon />}
        variant='outline'
        borderColor={AppColors.secondary}
        color={AppColors.secondary}
        _hover={{ bg: AppColors.secondary, color: AppColors.white }}
        _active={{ bg: AppColors.secondary, color: AppColors.white }}
      >
        {buttonMsg}
      </MenuButton>
      <MenuList boxShadow='none'>
        {options.length ? (
          options.map(({ id, name }) => (
            <MenuItem key={id} onClick={() => handleSelectOption(id)}>
              {name}
            </MenuItem>
          ))
        ) : (
          <MenuItem isDisabled>{emptyOptionsMsg}</MenuItem>
        )}
      </MenuList>
    </Menu>
  );
};

function CQTopbar() {
  const navigate = useNavigate();

  const notificationContext = useContext(NotificationsContext);
  const {
    unReadNotification,
    setUnReadNotification,
    getAuditUpload,
  } = notificationContext;

  const { isOpen, onToggle } = useDisclosure();
  const authContext = useContext(AuthContext);
  const [newNotifications, setNewNotification] = React.useState([]);

  useEffect(() => {
    notificationContext.getNotifications(1).then(response => {
      if (response?.results) {
        const sorted = [...response.results].sort((a, b) => Number(a.read) - Number(b.read));
        setNewNotification(sorted);
        setUnReadNotification(response.unread_count ?? 0);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNotificationStatus = async n => {
    if (!n.read) await notificationContext.markRead(n.notification_user_id);
    if (n?.reference_chart) {
      const res = await getAuditUpload(n.reference_chart);
      const EDITABLE_STATUSES = ['AWAITING AUDIT', 'IN PROGRESS', 'CLIENT REBUTTAL', 'REBUTTAL IN PROGRESS'];
      const route = res && EDITABLE_STATUSES.includes(res.status)
        ? replaceRoute(ROUTES.AUDIT_SHEET, { uploadId: n.reference_chart })
        : replaceRoute(ROUTES.VIEW_AUDIT_SHEET, { uploadId: n.reference_chart });
      navigate(route);
    }
  };

  const handleMarkSingleRead = async (e, n) => {
    e.stopPropagation();
    if (!n.read) {
      await notificationContext.markRead(n.notification_user_id);
    }
    setNewNotification(prev =>
      prev.filter(notif => notif.notification_user_id !== n.notification_user_id)
    );
  };

  const handleMarkAllRead = async () => {
    await notificationContext.markAllRead();
    setNewNotification(prev => prev.map(n => ({ ...n, read: true })));
  };

  const loadNotification = async () => {
    const response = await notificationContext.loadNewNotification();
    if (!response) return;
    const sorted = [...(response.results ?? [])].sort(
      (a, b) => Number(a.read) - Number(b.read)
    );
    setNewNotification(sorted);
    setUnReadNotification(response.unread_count ?? 0);
  };

  useInterval(loadNotification, 5000);

  const IS_ADMIN = useMemo(() => authContext?.currentUser?.role === 'ADMIN', [authContext?.currentUser]);

  return (
    <Box w={'100%'}>
      <Flex
        bg={useColorModeValue('white', 'gray.800')}
        shadow={'md'}
        position={'relative'}
        color={useColorModeValue('gray.600', 'white')}
        minH={'60px'}
        borderBottom={1}
        borderStyle={'solid'}
        borderColor={useColorModeValue('gray.200', 'gray.900')}
        align={'center'}
      >
        <Flex
          flex={{ base: 1, md: 'auto' }}
          ml={{ base: -2 }}
          display={{ base: 'flex', md: 'none' }}
        >
          <IconButton
            onClick={onToggle}
            icon={
              isOpen ? <CloseIcon w={3} h={3} /> : <HamburgerIcon w={5} h={5} />
            }
            variant={'ghost'}
            aria-label={'Toggle Navigation'}
          />
        </Flex>
        <Flex paddingLeft={5} height={'80px'} flex={{ base: 1 }} alignItems={'center'}>
          <HStack
            cursor={'pointer'}
            onClick={() => {
              navigate(ROUTES.ASSIGN_AUDITS_V2);
            }}
          >
            <Box >
              <CodeQuickIcon style={{ height: '3rem', cursor: 'pointer' }} />
            </Box>
            <Text
              display={{ base: 'none', md: 'block' }}
              color={AppColors.secondary}
              fontSize={'2xl'}
              fontWeight={'bold'}
              fontFamily={FONT_FAMILY.baiJamurjee}
              style={{ marginLeft: '34px', marginRight: '28px' }}
              letterSpacing={'0.3px'}
            >
              Manager Portal
            </Text>
          </HStack>

          <Flex display={{ base: 'none', md: 'flex' }} mr={5}>
            <DesktopNav />
          </Flex>
        </Flex>

        <HStack alignItems={'center'} spacing={5} mr={2}>

          {IS_ADMIN && (
            <ViewOptionBtn
              buttonMsg='Open Portal'
              emptyOptionsMsg='No portals available'
              options={PORTAL_VIEWS} />
          )}

          <Menu autoSelect={false}>
            <MenuButton position={'relative'}>
              <BellIcon
                style={{
                  width: 40,
                  height: 40,
                  cursor: 'pointer',
                  display: 'inline-block',
                }}
              />
              {unReadNotification > 0 ? (
                <Circle
                  position={'absolute'}
                  top={-1}
                  p={3}
                  right={-3}
                  color={AppColors.white}
                  size="20px"
                  bg={AppColors.danger}
                  fontWeight="bold"
                  fontSize={'xs'}
                >
                  {unReadNotification}
                </Circle>
              ) : (
                ''
              )}
            </MenuButton>
            <MenuList shadow={'none'} border={'none'} bg={'transparent'}>
              {newNotifications?.filter(n => !n.read).length === 0 ? (
                <MenuItem
                  borderTopRadius={'md'}
                  bg={AppColors.white}
                  _hover={{ bg: AppColors.white }}
                >
                  <Box p={4}>No Notifications</Box>
                </MenuItem>
              ) : (
                <>
                  <Box
                    bg={AppColors.white}
                    px={4}
                    py={2}
                    borderTopRadius={'md'}
                    borderBottom={`1px solid ${AppColors.gray}`}
                    display={'flex'}
                    justifyContent={'flex-end'}
                  >
                    <Text
                      fontSize={'xs'}
                      color={AppColors.secondary}
                      cursor={'pointer'}
                      fontWeight={'semibold'}
                      _hover={{ textDecoration: 'underline' }}
                      onClick={handleMarkAllRead}
                    >
                      Mark all as read
                    </Text>
                  </Box>
                  {newNotifications.filter(n => !n.read).slice(0, 3).map((n, i) => (
                    <Box
                      key={`${i}-notification`}
                      display={'flex'}
                      bg={AppColors.white}
                      borderBottom={`1px solid ${AppColors.gray}`}
                    >
                      <Box
                        flex={1} cursor={'pointer'} px={4} py={2} overflow={'hidden'}
                        onClick={() => handleNotificationStatus(n)}
                      >
                        <HStack spacing={1} overflow={'hidden'} mb={'2px'}>
                          {n.message.startsWith('@') && (
                            <Text fontWeight={'semibold'} fontSize={'sm'} color={AppColors.secondary} whiteSpace={'nowrap'}>
                              {toTitleCase(`${n?.sender?.first_name} ${n?.sender?.last_name}`)}
                            </Text>
                          )}
                          <Text fontSize={'sm'} color={'black'} noOfLines={1}>
                            {n.message.startsWith('@') ? n.message?.substring(1) : n.message}
                          </Text>
                        </HStack>
                        <Text color={AppColors.darkGray} fontSize={'11px'}>
                          {formatDistanceStrict(new Date(n.created_at), new Date(), { addSuffix: true })}
                        </Text>
                      </Box>
                      <Box
                        w={'44px'} minW={'44px'}
                        display={'flex'} alignItems={'center'} justifyContent={'center'}
                        borderLeft={`1px solid ${AppColors.gray}`}
                        cursor={'pointer'} _hover={{ bg: 'gray.100' }}
                        onClick={e => handleMarkSingleRead(e, n)}
                      >
                        <CloseIcon w={2} h={2} color={AppColors.darkGray} />
                      </Box>
                    </Box>
                  ))}
                </>
              )}

              <MenuItem
                _hover={{ bg: AppColors.secondary }}
                bg={AppColors.secondary}
                py={3}
                color={AppColors.white}
                borderBottomRadius={'md'}
                display={'flex'}
                justifyContent={'center'}
                onFocusCapture={'false'}
                onClick={() => navigate(ROUTES.NOTIFICATIONS)}
              >
                <Center fontSize={'sm'}>View all</Center>
              </MenuItem>
            </MenuList>
          </Menu>

          <Menu>
            <MenuButton
              px={4}
              py={2}
              transition="all 0.2s"
              borderRadius="md"
              _hover={{ bg: 'gray.100' }}
            >
              <HStack alignItems={'center'}>
                <Text fontWeight={'bold'} color={AppColors.secondary}>
                  {authContext?.currentUser?.email ?? 'N/A'}
                </Text>
                <ChevronDownIcon />
              </HStack>
            </MenuButton>
            <MenuList
              outline={'none'}
              border={'none'}
              shadow={'md'}
              padding={0}
            >
              <MenuItem
                onClick={() => navigate(ROUTES.ACCOUNT)}
                icon={<UserIcon style={{ width: 15, height: 15 }} />}
                height={'50px'}
                fontSize={'16px'}
              >
                My Account
              </MenuItem>
              {/* <MenuItem
                onClick={() => {
                  navigate(ROUTES.HELP);
                }}
                height={'50px'}
                fontSize={'16px'}
                icon={<HelpIcon style={{ width: 15, height: 15 }} />}
              >
                Help
              </MenuItem> */}
              <MenuItem
                onClick={authContext.onLogout}
                fontSize={'16px'}
                icon={<LogoutIcon style={{ width: 15, height: 15 }} />}
                height={'50px'}
              >
                Logout
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <MobileNav />
      </Collapse>
    </Box>
  );
}

const DesktopNavItem = ({ navItem }) => {
  // const linkColor = useColorModeValue('gray.600', 'gray.200');
  const linkHoverColor = 'white';
  const popoverContentBgColor = useColorModeValue('white', 'gray.800');

  const matchObject = useMatch(navItem.href);
  const isActive = !!matchObject;

  return (
    <Box ml={5}>
      <Popover trigger={'hover'} placement={'bottom-start'}>
        <PopoverTrigger>
          <NavLink to={navItem.href ?? '#'}>
            <Box
              p={3}
              borderRadius={'md'}
              fontSize="md"
              textAlign={'center'}
              bgColor={isActive ? AppColors.secondary : null}
              color={isActive ? 'white' : AppColors.black}
              _hover={{
                textDecoration: 'none',
                color: linkHoverColor,
                bgColor: AppColors.secondary,
                transition: 'all 0.5s ease',
              }}
            >
              <Text
                fontFamily={FONT_FAMILY.baiJamurjee}
                letterSpacing={'0.3px'}
                p={1}
                px={2}
                fontSize={'18px'}
                fontWeight={700}
              >
                {navItem.label}
              </Text>
            </Box>
          </NavLink>
        </PopoverTrigger>

        {navItem.children && (
          <PopoverContent
            border={0}
            boxShadow={'xl'}
            bg={popoverContentBgColor}
            p={4}
            rounded={'xl'}
            minW={'sm'}
          >
            <Stack>
              {navItem.children.map(child => (
                <DesktopSubNav key={child.label} {...child} />
              ))}
            </Stack>
          </PopoverContent>
        )}
      </Popover>
    </Box>
  );
};

const DesktopNav = () => {
  return (
    <HStack spacing={4}>
      {NAV_ITEMS.map((navItem, index) => {
        return (
          <DesktopNavItem
            key={`nav-bar-${navItem.href}-${index}`}
            style={{ fontWeight: 'bold' }}
            navItem={navItem}
          />
        );
      })}
    </HStack>
  );
};

const DesktopSubNav = ({ label, href, subLabel }) => {
  return (
    <Link
      href={href}
      role={'group'}
      display={'block'}
      p={2}
      rounded={'md'}
      _hover={{ bg: useColorModeValue('pink.50', 'gray.900') }}
    >
      <Stack direction={'row'} align={'center'}>
        <Box>
          <Text
            transition={'all .3s ease'}
            _groupHover={{ color: 'pink.400' }}
            fontWeight={500}
          >
            {label}
          </Text>
          <Text fontSize={'sm'}>{subLabel}</Text>
        </Box>
        <Flex
          transition={'all .3s ease'}
          transform={'translateX(-10px)'}
          opacity={0}
          _groupHover={{ opacity: '100%', transform: 'translateX(0)' }}
          justify={'flex-end'}
          align={'center'}
          flex={1}
        >
          <Icon color={'pink.400'} w={5} h={5} as={ChevronRightIcon} />
        </Flex>
      </Stack>
    </Link>
  );
};

const MobileNav = () => {
  return (
    <Stack
      bg={useColorModeValue('white', 'gray.800')}
      p={4}
      display={{ md: 'none' }}
    >
      {NAV_ITEMS.map(navItem => (
        <MobileNavItem key={navItem.label} {...navItem} />
      ))}
    </Stack>
  );
};

const MobileNavItem = ({ label, children, href }) => {
  const { isOpen, onToggle } = useDisclosure();

  return (
    <Stack spacing={4} onClick={children && onToggle}>
      <NavLink to={href ?? '#'}>
        <Flex
          py={2}
          justify={'space-between'}
          align={'center'}
          _hover={{
            textDecoration: 'none',
          }}
        >
          <Text
            fontWeight={600}
            color={useColorModeValue('gray.600', 'gray.200')}
          >
            {label}
          </Text>
          {children && (
            <Icon
              as={ChevronDownIcon}
              transition={'all .25s ease-in-out'}
              transform={isOpen ? 'rotate(180deg)' : ''}
              w={6}
              h={6}
            />
          )}
        </Flex>
      </NavLink>

      <Collapse in={isOpen} animateOpacity style={{ marginTop: '0!important' }}>
        <Stack
          mt={2}
          pl={4}
          borderLeft={1}
          borderStyle={'solid'}
          borderColor={useColorModeValue('gray.200', 'gray.700')}
          align={'start'}
        >
          {children &&
            children.map(child => (
              <NavLink key={child.label} py={2} to={child.href}>
                {child.label}
              </NavLink>
            ))}
        </Stack>
      </Collapse>
    </Stack>
  );
};

export default withProvider(NotificationProvider, CQTopbar);

const NAV_ITEMS = [
  {
    label: 'Assign Audits',
    href: ROUTES.ASSIGN_AUDITS_V2,
  },
  {
    label: 'My Audits',
    href: ROUTES.MY_AUDITS,
  },
  {
    label: 'Reports',
    href: ROUTES.REPORT,
  },
  {
    label: 'Teams',
    href: ROUTES.TEAM,
  },
  {
    label: 'Accounts',
    href: ROUTES.ACCOUNTS,
  },
  {
    label: 'Statistics',
    href: ROUTES.STATISTICS
  }
];
